import React, { useCallback, useEffect, useRef, useState } from "react";
import { FaEllipsisH, FaTimes, FaEye, FaEyeSlash, FaLink, FaTrash, FaArchive, FaBoxOpen } from "react-icons/fa";
import { Drawer, EmptyState, MascotLoadingOverlay } from "../shared";
import { TextInput } from "../atoms";
import {
  useGetTaskQuery,
  useUpdateTaskMutation,
  useWatchTaskMutation,
  useUnwatchTaskMutation,
  useDeleteTaskMutation,
  useArchiveTaskMutation,
  useUnarchiveTaskMutation,
  useDeleteTaskAttachmentMutation,
} from "../../store";
import useAuth from "../../Hooks/useAuth";
import { useAlert } from "../../context/AlertContext";
import TaskFieldsPanel from "./TaskFieldsPanel";
import TaskDescription from "./TaskDescription";
import TaskComments from "./TaskComments";
import TaskActivity from "./TaskActivity";
import TaskSubtasks, { ParentBreadcrumb } from "./TaskSubtasks";
import TaskChecklist from "./TaskChecklist";
import TaskConfirmModal, { OVER_DRAWER_Z } from "./TaskConfirmModal";
import FileDropzone from "./FileDropzone";
import AttachmentList from "./AttachmentList";
import useAttachmentUpload, { useTaskImageResolver } from "./useAttachmentUpload";
import { NestedOverlayProvider, DirtyDraftProvider, useDirtyDraft } from "./nestedOverlay";
import { colorClass, archiveBlockReason, formatDateTime } from "./taskConstants";

// İş detay çekmecesi (?is=FAB-12). Alan kayıtları tek yoldan: saveField -> updateTask.
// Contract: BayinerIOT-Workspace-/docs/api-contracts/tasks.md

export const CONFLICT_MESSAGE = "İş bu arada değişti, güncel hali yüklendi.";
const errText = (e, fallback) => e?.data?.message || e?.message || fallback;

async function copyText(text) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) {
    // aşağıdaki yedek yola düş
  }
  try {
    const el = document.createElement("textarea");
    el.value = text;
    el.setAttribute("readonly", "");
    el.className = "fixed -left-[9999px]";
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand && document.execCommand("copy");
    document.body.removeChild(el);
    return Boolean(ok);
  } catch (e) {
    return false;
  }
}

function TaskTitle({ title, canEdit, onSave }) {
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState("");
  const savingRef = useRef(false);
  useDirtyDraft(draft !== null && draft !== title);

  const commit = async () => {
    if (draft === null || savingRef.current) return;
    const v = draft.trim();
    if (!v || v.length > 200) {
      setError("Başlık 1–200 karakter olmalı.");
      return;
    }
    if (v === title) {
      setDraft(null);
      return;
    }
    savingRef.current = true;
    const ok = await onSave(v);
    savingRef.current = false;
    if (ok) setDraft(null);
  };

  if (draft !== null) {
    return (
      <TextInput
        autoFocus
        aria-label="Başlık"
        value={draft}
        placeholder="Başlık"
        maxLength={200}
        error={Boolean(error)}
        errorMessage={error}
        className="!py-2 text-lg font-SemiBold"
        onChange={(e) => {
          setDraft(e.target.value);
          setError("");
        }}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
          } else if (e.key === "Escape") {
            e.preventDefault();
            e.stopPropagation(); // çekmecenin Esc kapatmasına gitmesin
            setDraft(null);
            setError("");
          }
        }}
      />
    );
  }

  if (!canEdit) {
    return <h2 className="text-lg font-SemiBold text-gray-800 break-words">{title}</h2>;
  }
  return (
    <h2 className="text-lg font-SemiBold text-gray-800 break-words">
      <button
        type="button"
        aria-label="Başlığı düzenle"
        onClick={() => setDraft(title)}
        className="text-left w-full rounded-md px-1 -mx-1 hover:bg-gray-100"
      >
        {title}
      </button>
    </h2>
  );
}

function HeaderMenu({ canDelete, onCopy, onDelete }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const item = "w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50";
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        aria-label="Diğer işlemler"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="w-9 h-9 rounded-xl flex items-center justify-center text-slate-500 hover:text-slate-700 hover:bg-slate-200"
      >
        <FaEllipsisH className="w-4 h-4" />
      </button>
      {open && (
        <div role="menu" className="absolute right-0 mt-1 w-44 rounded-xl border border-gray-200 bg-white shadow-lg py-1 z-10">
          <button
            type="button"
            role="menuitem"
            className={`${item} text-gray-700`}
            onClick={() => {
              setOpen(false);
              onCopy();
            }}
          >
            <FaLink className="w-3.5 h-3.5" aria-hidden="true" />
            Linki kopyala
          </button>
          {canDelete && (
            <button
              type="button"
              role="menuitem"
              className={`${item} text-fifth`}
              onClick={() => {
                setOpen(false);
                onDelete();
              }}
            >
              <FaTrash className="w-3.5 h-3.5" aria-hidden="true" />
              Sil
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function TaskAttachments({ task }) {
  const { auth } = useAuth();
  const { showError } = useAlert();
  const { upload } = useAttachmentUpload(task._id);
  const [deleteAttachment] = useDeleteTaskAttachmentMutation();
  const [progress, setProgress] = useState(null);
  const canComment = Boolean(task.can?.comment);
  const list = (task.attachments || []).filter((a) => !a.comment);

  const onFiles = async (files) => {
    for (const file of files) {
      try {
        setProgress({ name: file.name, pct: 0 });
        // eslint-disable-next-line no-await-in-loop
        await upload(file, { onProgress: (pct) => setProgress({ name: file.name, pct }) });
      } catch (e) {
        showError(errText(e, "Dosya yüklenemedi."));
      }
    }
    setProgress(null);
  };

  const onDelete = async (att) => {
    try {
      await deleteAttachment({ token: auth?.accessToken, attachmentId: att._id, task: task._id }).unwrap();
    } catch (e) {
      showError(errText(e, "Ek silinemedi."));
    }
  };

  if (!canComment && !list.length) return null;
  return (
    <section className="space-y-2">
      <h3 className="text-sm font-SemiBold text-gray-700">Ekler {list.length > 0 && `(${list.length})`}</h3>
      <AttachmentList attachments={list} onDelete={onDelete} modalZIndex={OVER_DRAWER_Z} />
      {progress && (
        <div className="space-y-1" data-testid="upload-progress">
          <p className="text-xs text-gray-500 truncate">
            {progress.name} yükleniyor... %{progress.pct}
          </p>
          <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
            <div className="h-full bg-primary transition-all" style={{ width: `${progress.pct}%` }} />
          </div>
        </div>
      )}
      {canComment && <FileDropzone onFiles={onFiles} disabled={Boolean(progress)} />}
    </section>
  );
}

const TABS = [
  { id: "comments", label: "Yorumlar" },
  { id: "activity", label: "Geçmiş" },
];

// 1.7 — arşivdeki iş değiştirilemez: alanlar salt okunur (yorum/ek açık kalır).
const readOnlyIfArchived = (task) =>
  task.archivedAt ? { ...task, can: { ...task.can, edit: false, assign: false } } : task;

function ArchivedBanner({ task, canUnarchive, busy, onUnarchive }) {
  return (
    <div
      className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
      data-testid="task-archived-banner"
    >
      <span className="inline-flex items-center gap-2">
        <FaArchive className="w-3.5 h-3.5" aria-hidden="true" />
        Bu iş arşivde{task.archivedAt ? ` (${formatDateTime(task.archivedAt)})` : ""} — değiştirilemez.
      </span>
      {canUnarchive && (
        <button
          type="button"
          onClick={onUnarchive}
          disabled={busy}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-SemiBold bg-white border border-amber-200 hover:bg-amber-100 disabled:opacity-50"
        >
          <FaBoxOpen className="w-3 h-3" aria-hidden="true" />
          Arşivden çıkar
        </button>
      )}
    </div>
  );
}

function TaskDetailBody({ task: rawTask, saveField, onOpenTask, archiveBusy, onUnarchive }) {
  const task = readOnlyIfArchived(rawTask);
  const [tab, setTab] = useState("comments");
  const resolveImage = useTaskImageResolver();
  const { upload } = useAttachmentUpload(task._id);
  const canEdit = Boolean(task.can?.edit);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <div className="flex-1 min-w-0 space-y-6">
        {task.archivedAt && (
          <ArchivedBanner
            task={task}
            canUnarchive={Boolean(rawTask.can?.edit)}
            busy={archiveBusy}
            onUnarchive={onUnarchive}
          />
        )}
        {task.parent && (
          <div className="-mb-4">
            <ParentBreadcrumb parent={task.parent} onOpenTask={onOpenTask} />
          </div>
        )}
        <TaskTitle title={task.title} canEdit={canEdit} onSave={(title) => saveField({ title })} />
        <TaskDescription
          html={task.description}
          canEdit={canEdit}
          resolveImage={resolveImage}
          onUploadImage={async (file) => (await upload(file))?._id}
          onSave={(description) => saveField({ description })}
        />
        <TaskSubtasks task={task} onOpenTask={onOpenTask} />
        <TaskChecklist task={task} />
        <TaskAttachments task={task} />
        <section>
          <div className="flex gap-4 border-b border-gray-200 mb-4" role="tablist">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={tab === t.id}
                onClick={() => setTab(t.id)}
                className={`pb-2 -mb-px text-sm font-Medium border-b-2 ${
                  tab === t.id ? "border-main text-main" : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                {t.label}
                {t.id === "comments" && task.commentCount > 0 && ` (${task.commentCount})`}
              </button>
            ))}
          </div>
          {/* Yorumlar gizlenir, sökülmez: yazılan yorum ve bekleyen ekler sekme değişiminde korunur */}
          <div hidden={tab !== "comments"}>
            <TaskComments taskId={task._id} canComment={Boolean(task.can?.comment)} />
          </div>
          {tab === "activity" && <TaskActivity activity={task.activity || []} />}
        </section>
      </div>
      <aside className="lg:w-72 shrink-0">
        <TaskFieldsPanel task={task} saveField={saveField} />
      </aside>
    </div>
  );
}

// onOpenTask(key): başka işi (alt iş / üst iş) aynı çekmecede açar (?is=<key>).
function TaskDetailDrawer({ taskKey, open, onClose, onOpenTask }) {
  const { auth } = useAuth();
  const token = auth?.accessToken;
  const { showWarning, showError, showSuccess } = useAlert();
  const { data, isLoading, error } = useGetTaskQuery(
    { token, key: taskKey },
    { skip: !open || !taskKey }
  );
  const [updateTask] = useUpdateTaskMutation();
  const [watchTask, { isLoading: watching }] = useWatchTaskMutation();
  const [unwatchTask, { isLoading: unwatching }] = useUnwatchTaskMutation();
  const [deleteTask, { isLoading: deleting }] = useDeleteTaskMutation();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [archiveTask, { isLoading: archiving }] = useArchiveTaskMutation();
  const [unarchiveTask, { isLoading: unarchiving }] = useUnarchiveTaskMutation();
  const [confirmArchive, setConfirmArchive] = useState(false);

  const task = data?.data?.task;
  const notFound = !task && error && (error.status === 404 || error.originalStatus === 404);

  // Çekmece içi katmanlar (onay, görsel önizleme) açıkken Esc/arka plan çekmeceyi kapatmaz.
  // Kaydedilmemiş taslak (başlık/açıklama/yorum) varsa kapatmadan önce onay sorulur.
  const overlaysRef = useRef(0);
  const draftsRef = useRef(new Set());
  const [confirmClose, setConfirmClose] = useState(false);
  const guardedClose = useCallback(() => {
    if (overlaysRef.current > 0) return;
    if (draftsRef.current.size > 0) {
      setConfirmClose(true);
      return;
    }
    onClose();
  }, [onClose]);

  // Bilinen en güncel iş: sorgu sonucu ya da son kayıt yanıtı (hangisinin version'ı büyükse).
  const latestRef = useRef(null);
  if (
    task &&
    (!latestRef.current ||
      latestRef.current._id !== task._id ||
      (task.version ?? 0) >= (latestRef.current.version ?? 0))
  ) {
    latestRef.current = task;
  }
  const queueRef = useRef(Promise.resolve());

  // Tek kayıt yolu, sıralı: her istek bir öncekinin bitmesini bekler ve version'ı
  // gönderim anında latestRef'ten okur. `changes` fonksiyon da olabilir:
  // (güncel iş) => changes | null — ör. etiket ekle/çıkar güncel etiketlerden hesaplanır.
  // 409'da güncel iş (error.data.data.task) latestRef'e ve (tasksApi'de) cache'e yazılır.
  const saveField = useCallback(
    (changes) => {
      const run = async () => {
        const cur = latestRef.current;
        if (!cur) return false;
        const payload = typeof changes === "function" ? changes(cur) : changes;
        if (!payload) return true;
        try {
          const res = await updateTask({
            token, id: cur._id, version: cur.version, changes: payload, task: cur._id,
            ...(cur.parent?._id ? { parent: cur.parent._id } : {}),
          }).unwrap();
          const fresh = res?.data?.task;
          if (fresh && fresh._id === latestRef.current?._id) latestRef.current = fresh;
          return true;
        } catch (e) {
          const fresh = e?.data?.data?.task;
          if (fresh && fresh._id === latestRef.current?._id) latestRef.current = fresh;
          if (e?.status === 409) showWarning(CONFLICT_MESSAGE);
          else showError(errText(e, "Değişiklik kaydedilemedi."));
          return false;
        }
      };
      const p = queueRef.current.then(run, run);
      queueRef.current = p.catch(() => {});
      return p;
    },
    [token, updateTask, showWarning, showError]
  );

  const toggleWatch = async () => {
    try {
      const fn = task.isWatching ? unwatchTask : watchTask;
      await fn({ token, id: task._id }).unwrap();
    } catch (e) {
      showError(errText(e, "İşlem başarısız."));
    }
  };

  const copyLink = async () => {
    const ok = await copyText(window.location.href);
    if (ok) showSuccess("Link kopyalandı.");
    else showError("Link kopyalanamadı.");
  };

  const doDelete = async () => {
    try {
      await deleteTask({
        token, id: task._id, task: task._id, ...(task.parent?._id ? { parent: task.parent._id } : {}),
      }).unwrap();
      setConfirmDelete(false);
      showSuccess("İş silindi.");
      onClose();
    } catch (e) {
      showError(errText(e, "İş silinemedi."));
    }
  };

  const doArchive = async () => {
    try {
      await archiveTask({ token, id: task._id }).unwrap();
      setConfirmArchive(false);
      showSuccess("İş arşive atıldı.");
    } catch (e) {
      showError(errText(e, "İş arşivlenemedi."));
    }
  };

  const doUnarchive = async () => {
    try {
      await unarchiveTask({ token, id: task._id }).unwrap();
      showSuccess("İş arşivden çıkarıldı.");
    } catch (e) {
      showError(errText(e, "İş arşivden çıkarılamadı."));
    }
  };

  // Arşiv onayı: birlikte gidecek alt işler ve incelemedekilerin tamamlanacağı söylenir.
  const archiveMessage = () => {
    if (!task) return "";
    const subs = (task.subtasks || []).filter((s) => !s.archivedAt);
    const inReview = [task, ...subs].filter((x) => x.status === "in_review").length;
    return [
      `${task.key} arşive atılacak ve panodan/listeden kalkacak.`,
      subs.length > 0 ? `${subs.length} alt işi de onunla birlikte arşive gidecek.` : "",
      inReview > 0 ? `İncelemedeki ${inReview} iş Tamamlandı yapılacak.` : "",
      "Arşiv görünümünden geri alınabilir.",
    ]
      .filter(Boolean)
      .join(" ");
  };

  let body;
  if (task) {
    body = (
      <TaskDetailBody
        key={task._id}
        task={task}
        saveField={saveField}
        onOpenTask={onOpenTask}
        archiveBusy={unarchiving}
        onUnarchive={doUnarchive}
      />
    );
  } else if (notFound) {
    body = <EmptyState message="İş bulunamadı" suggestion="İş silinmiş ya da görme yetkiniz olmayabilir." />;
  } else if (error) {
    body = <EmptyState message="İş yüklenemedi" suggestion={errText(error, "Lütfen tekrar deneyin.")} />;
  } else if (isLoading || open) {
    body = (
      <div className="relative min-h-[320px]">
        <MascotLoadingOverlay message="İş yükleniyor..." />
        <div className="space-y-3 animate-pulse">
          <div className="h-6 w-2/3 rounded bg-gray-100" />
          <div className="h-24 rounded bg-gray-100" />
          <div className="h-40 rounded bg-gray-100" />
        </div>
      </div>
    );
  }

  const busyWatch = watching || unwatching;
  const archiveReason = archiveBlockReason(task);

  return (
    <Drawer open={open} onClose={guardedClose} width="xl">
      <NestedOverlayProvider counterRef={overlaysRef}>
      <DirtyDraftProvider draftsRef={draftsRef}>
      <Drawer.Header className="px-4 sm:px-5 py-3 bg-slate-50/80 border-b border-slate-200">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-base font-SemiBold text-slate-800 shrink-0" data-testid="task-drawer-key">
              {task?.key || taskKey}
            </span>
            {task?.project && (
              <span className={`truncate px-2 py-0.5 rounded-md text-xs font-Medium ${colorClass(task.project.color)}`}>
                {task.project.name}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {task && (
              <>
                <button
                  type="button"
                  onClick={toggleWatch}
                  disabled={busyWatch}
                  className="inline-flex items-center gap-1.5 px-3 h-9 rounded-xl text-sm font-Medium text-slate-600 hover:bg-slate-200 disabled:opacity-50"
                >
                  {task.isWatching ? (
                    <FaEyeSlash className="w-3.5 h-3.5" aria-hidden="true" />
                  ) : (
                    <FaEye className="w-3.5 h-3.5" aria-hidden="true" />
                  )}
                  {task.isWatching ? "Takibi bırak" : "Takip et"}
                </button>
                {task.can?.edit && !task.archivedAt && (
                  // Pasifken neden title'da (span: pasif düğme fareyi yakalamaz)
                  <span title={archiveReason || "Arşive at"} className="inline-flex">
                    <button
                      type="button"
                      onClick={() => setConfirmArchive(true)}
                      disabled={Boolean(archiveReason) || archiving}
                      className="inline-flex items-center gap-1.5 px-3 h-9 rounded-xl text-sm font-Medium text-slate-600 hover:bg-slate-200 disabled:opacity-40 disabled:hover:bg-transparent"
                    >
                      <FaArchive className="w-3.5 h-3.5" aria-hidden="true" />
                      Arşive at
                    </button>
                  </span>
                )}
                <HeaderMenu
                  canDelete={Boolean(task.can?.delete)}
                  onCopy={copyLink}
                  onDelete={() => setConfirmDelete(true)}
                />
              </>
            )}
            <button
              type="button"
              onClick={guardedClose}
              aria-label="Kapat"
              className="w-9 h-9 rounded-xl flex items-center justify-center text-slate-500 hover:text-slate-700 hover:bg-slate-200"
            >
              <FaTimes className="w-4 h-4" />
            </button>
          </div>
        </div>
      </Drawer.Header>
      <Drawer.Body className="p-4 sm:p-5">{body}</Drawer.Body>
      <TaskConfirmModal
        open={confirmDelete}
        title="İşi sil"
        message={`${task?.key || ""} silinecek ve listelerden kalkacak. Emin misiniz?`}
        busy={deleting}
        onConfirm={doDelete}
        onClose={() => setConfirmDelete(false)}
      />
      <TaskConfirmModal
        open={confirmArchive}
        title="Arşive at"
        message={archiveMessage()}
        confirmLabel="Arşive at"
        busy={archiving}
        busyLabel="Arşivleniyor..."
        onConfirm={doArchive}
        onClose={() => setConfirmArchive(false)}
      />
      <TaskConfirmModal
        open={confirmClose}
        title="Kaydedilmemiş değişiklikler"
        message="Kaydedilmemiş değişiklikler var. Kapatılsın mı?"
        confirmLabel="Kapat"
        onConfirm={() => {
          setConfirmClose(false);
          onClose();
        }}
        onClose={() => setConfirmClose(false)}
      />
      </DirtyDraftProvider>
      </NestedOverlayProvider>
    </Drawer>
  );
}

export default TaskDetailDrawer;
